import {
  type ListingClientTaskFragment,
  ListingClientTasksDocument,
  type ListingClientTasksQuery,
  type ListingClientTasksQueryVariables,
  type ListingInternalTaskFragment,
  ListingInternalTasksDocument,
  type ListingInternalTasksQuery,
  type ListingInternalTasksQueryVariables,
  type TasksForScopedUsersQuery,
  type TasksQuery,
} from "@src/__generated__/graphql";
import { TaskModel } from "@src/components/modules/resource-planning/tasks/listing/models";
import { TaskListingColumnsStore } from "@src/components/modules/resource-planning/tasks/listing/store/columns";
import { TasksBaseStore } from "@src/components/modules/resource-planning/tasks/store/base-store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import { action, computed, makeObservable, observable } from "mobx";

// NOTE: Just for legacy code sake, after we change all task components in linked tasks tab or notifications we can remove this
export type TaskType = NonNullable<
  TasksQuery["tasks"] | TasksForScopedUsersQuery["tasks"]
>["data"][0];
export type InternalTaskType = NonNullable<TasksQuery["tasks"]>["data"][0];
export type ScopedTaskType = NonNullable<
  TasksForScopedUsersQuery["tasks"]
>["data"][0];

export class TasksListingStore extends TasksBaseStore {
  @observable.ref
  horizontallyScrolled = false;

  @observable
  tasks: TaskModel[] = [];

  columnsStore: TaskListingColumnsStore;

  private tasksFetcher = new FetchHelper<
    ListingInternalTasksQuery,
    ListingInternalTasksQueryVariables
  >(ListingInternalTasksDocument);
  private tasksScopedFetcher = new FetchHelper<
    ListingClientTasksQuery,
    ListingClientTasksQueryVariables
  >(ListingClientTasksDocument);

  constructor(appStore: AppStore) {
    super(appStore);
    makeObservable(this);
    this.columnsStore = new TaskListingColumnsStore(appStore);
  }

  @computed
  get taskCount() {
    return this.tasks.length;
  }

  @computed
  get isFetching(): boolean {
    return (
      this.tasksFetcher.isLoading.value ||
      this.tasksScopedFetcher.isLoading.value
    );
  }

  @computed get subtasksOpen() {
    return this.tasks.every((task) => task.isOpen.value);
  }

  @action.bound
  toggleSubtasksOpen() {
    if (this.subtasksOpen) {
      for (const task of this.tasks) {
        task.isOpen.off();
      }
    } else {
      for (const task of this.tasks) {
        task.isOpen.on();
      }
    }
  }

  resetPagination(): void {
    super.resetPagination();
    this.tasks = [];
  }

  @action.bound
  async fetchTasks(): Promise<void> {
    if (!this.appStore.authStore.user?.type) return;
    const fetcher = {
      partner: this.tasksScopedFetcher,
      client: this.tasksScopedFetcher,
      internal: this.tasksFetcher,
    }[this.appStore.authStore.user.type];

    const [data, error] = await fetcher.fetch(this.filtersStore.queryParams);

    if (error || !data.tasks) return;

    this.addTasks(data.tasks.data);
    this.filtersStore.pagination.setFromPaginatorInfo(data.tasks.paginatorInfo);
    if (!this.filtersStore.pagination.canLoadMore) return;
    this.filtersStore.pagination.inc();
  }

  @action.bound addTasks(
    tasks: (ListingClientTaskFragment | ListingInternalTaskFragment)[],
  ) {
    const loadedIds = new Set(this.tasks.map(({ id }) => id));

    for (const task of tasks) {
      if (loadedIds.has(task.id)) continue;
      loadedIds.add(task.id);
      this.tasks.push(new TaskModel(task, this.appStore));
    }
  }

  @action.bound
  onAddSubtask(subtaskId: TaskModel["id"]) {
    this.appStore.taskDetailModalStore.disclosureState.onOpen({
      id: subtaskId,
      onChange: () => {
        this.resetPagination();
      },
      onDelete: () => {
        this.resetPagination();
      },
      onDuplicate: () => {
        this.resetPagination();
      },
      onTimeTrackingItemCreated: () => {
        this.resetPagination();
      },
    });
  }

  updateTask(
    updatedTask: {
      id: TaskModel["id"];
    } & Partial<ListingInternalTaskFragment>,
  ): void {
    for (const task of this.tasks) {
      for (const subtask of task.subtasks) {
        if (subtask.id !== updatedTask.id) continue;
        subtask.update(updatedTask);
        break;
      }

      if (task.id !== updatedTask.id) continue;
      task.update(updatedTask);
    }
  }

  removeTask(
    taskId: TaskModel["id"],
    parentId?: NonNullable<TaskModel["parent"]>["id"],
  ): void {
    if (parentId) {
      this.tasks.find(({ id }) => id === parentId)?.removeSubtask(taskId);
      return;
    }

    const taskIndex = this.tasks.findIndex(({ id }) => id === taskId);
    if (taskIndex === -1) return;
    this.tasks.splice(taskIndex, 1);
  }

  @action.bound
  handleScroll(e: React.UIEvent<HTMLDivElement>) {
    if (e.currentTarget.scrollLeft === 0 && !this.horizontallyScrolled) return;
    if (e.currentTarget.scrollLeft > 0 && this.horizontallyScrolled) return;
    this.horizontallyScrolled = e.currentTarget.scrollLeft > 0;
  }

  handleTaskClick(task: Partial<TaskModel>) {
    if (!task.id) return;

    this.appStore.taskDetailModalStore.disclosureState.onOpen({
      id: task.id,
      onChange: (updatedTask) =>
        this.updateTask({
          id: updatedTask.id,
          name: updatedTask.name,
          billable:
            "billable" in updatedTask ? updatedTask.billable : undefined,
          is_template: updatedTask.is_template,
          positions: updatedTask.positions.map((position) => ({
            ...position,
            spent_time:
              "spent_time" in position &&
              typeof position.spent_time === "number"
                ? position.spent_time
                : 0,
          })),
          priority: updatedTask.priority,
          deadline: updatedTask.deadline,
          ourWorkBudgetItem: updatedTask.ourWorkBudgetItem
            ? {
                id: updatedTask.ourWorkBudgetItem.id,
                title:
                  "title" in updatedTask.ourWorkBudgetItem
                    ? updatedTask.ourWorkBudgetItem.title
                    : "",
                project: updatedTask.ourWorkBudgetItem.project,
              }
            : undefined,
          parent: updatedTask.parent,
          task_template_group_id: updatedTask.task_template_group_id,
          scopes: "scopes" in updatedTask ? updatedTask.scopes : [],
          stats: "stats" in updatedTask ? updatedTask.stats : undefined,
          status: updatedTask.status,
        }),
      onDelete: (task) => this.removeTask(task.id, task.parent?.id),
      onDuplicate: (task) => {
        this.addTasks([task]);
      },
    });
  }
}
